import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog } from 'primereact/dialog'
import { Steps } from 'primereact/steps'
import { useImmer } from 'use-immer'

import { HandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { SupplierItemList } from '../../../data'
import { SummaryPanel } from './SummaryPanel'
import { UploadPanel } from './UploadPanel'
import { VerifyPanel } from './VerifyPanel'

export type SuppliersTableState = {
  statusDialog: {
    isVisible: boolean
    isRequestRunning: boolean
    location?: string
    retryAfter?: string
    isCreate: boolean
    isDelete: boolean
    isSupplierProcess: boolean
  }
}

type Props = {
  visible: boolean
  onHide: () => void
}

export const SupplierUploadDialog: React.FC<Props> = ({ onHide, visible }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [csvData, setCsvData] = useState<SupplierItemList | null>(null)
  const [error, setError] = useState<HandleableError | null>(null)
  const [footer, setFooter] = useState<React.ReactNode | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const [state, updateState] = useImmer<SuppliersTableState>({
    statusDialog: {
      isRequestRunning: false,
      isVisible: false,
      isCreate: false,
      isDelete: false,
      isSupplierProcess: false,
      location: undefined,
      retryAfter: undefined,
    },
  })

  const onHideStatus = () => {
    updateState(draft => {
      draft.statusDialog.isVisible = false
      draft.statusDialog.isRequestRunning = false
      draft.statusDialog.location = undefined
      draft.statusDialog.retryAfter = undefined
      draft.statusDialog.isSupplierProcess = false

      return draft
    })
  }

  const items = [
    { label: t('suppliers.table.upload.dialog.uploadPanel.title') },
    { label: t('suppliers.table.upload.dialog.verifyPanel.title') },
    { label: t('suppliers.table.upload.dialog.summaryPanel.title') },
  ]

  const handleClose = () => {
    setIsLoading(false)
    setError(null)
    onHideStatus()
    onHide()
    goToFirstStep()
  }

  const goToFirstStep = () => {
    setActiveIndex(0)
  }

  const goToNextStep = () => {
    // If max step, do not go forward
    if (activeIndex === items.length - 1) return
    setActiveIndex(prev => prev + 1)
  }

  const goToPrevStep = () => {
    // If step 0, do not go back
    if (activeIndex === 0) return
    setActiveIndex(prev => prev - 1)
  }

  const header = (
    <>
      {t('suppliers.table.upload.dialog.title')}
      <Steps model={items} activeIndex={activeIndex} className="mt-4" />
    </>
  )

  return (
    <Dialog
      onHide={handleClose}
      visible={visible}
      header={header}
      footer={footer}
      style={{ width: '60rem' }}
      breakpoints={{ [calculateBreakpointWidth(60)]: '90vw' }}
    >
      {activeIndex === 0 && (
        <UploadPanel
          setFooter={setFooter}
          goToNextStep={goToNextStep}
          setCsvData={setCsvData}
          setError={setError}
          setIsLoading={setIsLoading}
        />
      )}

      {activeIndex === 1 && (
        <VerifyPanel
          data={csvData!}
          error={error}
          isLoading={isLoading}
          setFooter={setFooter}
          goToNextStep={goToNextStep}
          goToPrevStep={goToPrevStep}
          setCsvData={setCsvData}
          setError={setError}
          setIsLoading={setIsLoading}
          updateState={updateState}
        />
      )}

      {activeIndex === 2 && (
        <SummaryPanel
          isCreate={state.statusDialog.isCreate}
          isDelete={state.statusDialog.isDelete}
          isSupplierProcess={state.statusDialog.isSupplierProcess}
          location={state.statusDialog.location!}
          visible={state.statusDialog.isVisible}
          retry={state.statusDialog.retryAfter}
          isLoading={isLoading}
          onExit={handleClose}
          setFooter={setFooter}
        />
      )}
    </Dialog>
  )
}
